import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Copy, Check, KeyRound, Zap, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import SEO from "../components/SEO";

const ENDPOINT_ROWS = [
    { method: "POST", path: "/api/factcheck",       desc: "Fact-check a text claim or detect a scam." },
    { method: "POST", path: "/api/factcheck/image", desc: "Analyze an image (screenshot, meme, headline)." },
    { method: "POST", path: "/api/factcheck/voice", desc: "Transcribe & fact-check an audio recording." },
    { method: "GET",  path: "/api/stats/truth-score", desc: "Live counters + verdict distribution." },
    { method: "GET",  path: "/api/trending",          desc: "Recent public fact-check results." },
];

const CURL_EXAMPLE = `curl -X POST https://real-check.org/api/factcheck \\
  -H "Authorization: Bearer rc_live_YOUR_KEY_HERE" \\
  -H "Content-Type: application/json" \\
  -d '{
    "claim": "Humans only use 10% of their brain.",
    "mode": "auto"
  }'`;

const RESPONSE_EXAMPLE = `{
  "check_id": "fc_abc123",
  "claim": "Humans only use 10% of their brain.",
  "verdict": "false",
  "confidence": 96,
  "summary": "Neuroimaging studies show humans use virtually all brain regions...",
  "red_flags": ["myth", "commonly misattributed"],
  "sources": [],
  "created_at": "2026-07-22T09:14:43Z"
}`;

const JS_FETCH_EXAMPLE = `// browser / Node.js
const res = await fetch("https://real-check.org/api/factcheck", {
  method: "POST",
  headers: {
    "Authorization": "Bearer " + REAL_CHECK_KEY,
    "Content-Type": "application/json",
  },
  body: JSON.stringify({
    claim: "The Great Wall is visible from space.",
    mode: "auto",
  }),
});
const data = await res.json();
console.log(data.verdict, data.confidence, data.summary);`;

const EXT_MANIFEST = `{
  "manifest_version": 3,
  "name": "Real-Check",
  "version": "1.0.0",
  "description": "Right-click any text to fact-check it with Real-Check.",
  "permissions": ["contextMenus", "storage", "activeTab", "scripting"],
  "host_permissions": ["https://real-check.org/*"],
  "background": { "service_worker": "background.js" },
  "action": { "default_title": "Real-Check" }
}`;

const EXT_BACKGROUND = `// background.js — Chrome extension service worker

const API = "https://real-check.org/api/factcheck";

// Hard-code your key or load it from chrome.storage
async function getKey() {
  const { rc_key } = await chrome.storage.local.get("rc_key");
  return rc_key;
}

chrome.runtime.onInstalled.addListener(() => {
  chrome.contextMenus.create({
    id: "rc-check",
    title: "Fact-check with Real-Check",
    contexts: ["selection"],
  });
});

chrome.contextMenus.onClicked.addListener(async (info, tab) => {
  if (info.menuItemId !== "rc-check" || !info.selectionText) return;
  const key = await getKey();
  if (!key) return alert("Add your Real-Check API key first (Options).");

  const res = await fetch(API, {
    method: "POST",
    headers: {
      "Authorization": "Bearer " + key,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ claim: info.selectionText, mode: "auto" }),
  });
  const data = await res.json();

  chrome.scripting.executeScript({
    target: { tabId: tab.id },
    func: (v) => alert("Real-Check verdict: " + v.verdict.toUpperCase()
      + " (" + v.confidence + "%)\\n\\n" + v.summary),
    args: [data],
  });
});`;

export default function ApiDocs() {
    return (
        <div className="max-w-5xl mx-auto px-6 lg:px-10 py-14">
            <SEO
                title="Real-Check API — Real-time fact checking for developers"
                description="REST API for AI-powered fact checking and scam detection. Text, image, and voice endpoints. Free tier: 1,000 requests/day. Chrome extension code sample included."
                path="/docs/api"
                keywords="fact check API, misinformation API, scam detection API, real-time fact checking API, chrome extension fact check"
            />
            <div className="mb-12">
                <div className="inline-flex items-center gap-2 rounded-full border border-primary/40 bg-primary/15 px-3 py-1 text-[11px] font-semibold text-accent mb-4">
                    <Zap className="h-3 w-3 text-primary" /> Developer docs
                </div>
                <h1 className="font-display text-4xl md:text-6xl text-accent tracking-tight leading-[1.03]">
                    Real-Check API
                </h1>
                <p className="mt-5 text-lg text-muted-foreground max-w-2xl leading-relaxed">
                    A REST API for the same fact-check + scam-detection engine that powers real-check.org.
                    Text, image, and voice — one endpoint each, one JSON contract.
                </p>
                <div className="mt-8 flex flex-wrap gap-3">
                    <Link to="/developers">
                        <Button className="rounded-full h-11 px-5 bg-primary hover:bg-primary/90 text-primary-foreground font-semibold" data-testid="docs-get-key-btn">
                            <KeyRound className="h-4 w-4 mr-2" /> Get your API key
                        </Button>
                    </Link>
                    <a href="#badge">
                        <Button variant="secondary" className="rounded-full h-11 px-5">
                            Embeddable badge ↓
                        </Button>
                    </a>
                </div>
            </div>

            {/* BASE URL & AUTH */}
            <Section title="1. Base URL & authentication" testid="docs-auth-section">
                <p className="text-muted-foreground leading-relaxed mb-4">
                    All requests go through <code className="text-accent">https://real-check.org/api</code>. Pass your
                    key on every request as a bearer token:
                </p>
                <CodeBlock code={`Authorization: Bearer rc_live_YOUR_KEY_HERE`} lang="http" />
                <p className="text-xs text-muted-foreground mt-3">
                    Keys look like <code className="text-accent">rc_live_</code> followed by 32 hex characters.
                    Anonymous requests (no key) still work but are rate-limited to 5/hour per IP.
                </p>
            </Section>

            {/* ENDPOINTS */}
            <Section title="2. Endpoints" testid="docs-endpoints-section">
                <Card className="card-surface rounded-xl overflow-hidden">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-left text-xs uppercase tracking-widest text-muted-foreground">
                                <th className="px-4 py-3">Method</th>
                                <th className="px-4 py-3">Path</th>
                                <th className="px-4 py-3">What it does</th>
                            </tr>
                        </thead>
                        <tbody>
                            {ENDPOINT_ROWS.map((r) => (
                                <tr key={r.path} className="border-t border-white/5">
                                    <td className="px-4 py-3">
                                        <span className={`font-mono text-[11px] px-2 py-0.5 rounded-full ${r.method === "GET" ? "bg-blue-500/15 text-blue-300 border border-blue-400/30" : "bg-emerald-500/15 text-emerald-300 border border-emerald-400/30"}`}>
                                            {r.method}
                                        </span>
                                    </td>
                                    <td className="px-4 py-3 font-mono text-accent">{r.path}</td>
                                    <td className="px-4 py-3 text-muted-foreground">{r.desc}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </Card>
            </Section>

            {/* QUICKSTART */}
            <Section title="3. Quickstart · cURL" testid="docs-curl-section">
                <CodeBlock code={CURL_EXAMPLE} lang="bash" />
                <p className="text-xs text-muted-foreground mt-3">
                    <code className="text-accent">mode</code> accepts <code>auto</code> (default), <code>factcheck</code>, or <code>scam</code>.
                </p>
            </Section>

            <Section title="4. Response shape" testid="docs-response-section">
                <CodeBlock code={RESPONSE_EXAMPLE} lang="json" />
                <div className="grid md:grid-cols-2 gap-4 mt-4 text-sm">
                    <div>
                        <div className="text-accent font-semibold mb-1">Fact-check verdicts</div>
                        <div className="text-muted-foreground">
                            <code>true</code> · <code>mostly_true</code> · <code>misleading</code> · <code>false</code> · <code>unverified</code>
                        </div>
                    </div>
                    <div>
                        <div className="text-accent font-semibold mb-1">Scam verdicts</div>
                        <div className="text-muted-foreground">
                            <code>safe</code> · <code>likely_safe</code> · <code>suspicious</code> · <code>scam</code> · <code>unverified</code>
                        </div>
                    </div>
                </div>
            </Section>

            <Section title="5. JavaScript / fetch" testid="docs-js-section">
                <CodeBlock code={JS_FETCH_EXAMPLE} lang="js" />
            </Section>

            {/* EMBEDDABLE BADGE — NEW */}
            <Section title="6. Embeddable verdict badge — one snippet, any site" testid="docs-badge-section" id="badge">
                <p className="text-muted-foreground leading-relaxed mb-4">
                    Drop a live &quot;Verified by Real-Check&quot; badge on any article or blog post.
                    No key required — the badge is cached per-claim for 24 hours and rate-limited to protect the pipeline.
                </p>
                <CodeBlock
                    code={`<!-- Place anywhere in the article -->
<div class="real-check"
     data-claim="Humans only use 10% of their brain."></div>

<!-- Once per page, near </body> -->
<script async src="https://real-check.org/badge.js"></script>`}
                    lang="html"
                />
                <div className="mt-4 flex items-center gap-3 flex-wrap">
                    <div className="text-xs text-muted-foreground">Renders as →</div>
                    <a
                        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold"
                        style={{ background: "#3d0e0e", color: "#ff6b6b", border: "1px solid rgba(255,255,255,0.12)" }}
                    >
                        <span className="w-4 h-4 rounded-full inline-flex items-center justify-center text-[10px] font-bold" style={{ background: "#ff6b6b", color: "#0a0a0a" }}>✕</span>
                        False
                        <span className="opacity-70 font-medium">96%</span>
                        <span className="opacity-70 text-[10px] tracking-widest ml-1">· REAL-CHECK</span>
                    </a>
                </div>
                <p className="text-xs text-muted-foreground mt-3">
                    Auto-detects new nodes via <code className="text-accent">MutationObserver</code> so it works on React / Vue / Next pages.
                    Click the badge to open the full verdict on real-check.org.
                </p>
            </Section>

            {/* EXTENSION */}
            <Section title="7. Chrome extension — full working example" testid="docs-extension-section" id="extension">
                <p className="text-muted-foreground leading-relaxed mb-4">
                    Right-click any highlighted text on any page → fact-check it in a single click.
                    Two files. Copy, paste, load unpacked, done.
                </p>
                <div className="space-y-4">
                    <div>
                        <h4 className="font-display text-lg text-accent mb-2">manifest.json</h4>
                        <CodeBlock code={EXT_MANIFEST} lang="json" />
                    </div>
                    <div>
                        <h4 className="font-display text-lg text-accent mb-2">background.js</h4>
                        <CodeBlock code={EXT_BACKGROUND} lang="js" />
                    </div>
                </div>
                <p className="text-xs text-muted-foreground mt-4">
                    Store the key with <code className="text-accent">chrome.storage.local.set(&#123;rc_key: &quot;rc_live_...&quot;&#125;)</code> from an options page.
                    Never bundle it in source you publish publicly.
                </p>
            </Section>

            {/* RATE LIMITS */}
            <Section title="8. Rate limits & quotas" testid="docs-limits-section">
                <div className="grid md:grid-cols-3 gap-4">
                    <QuotaCard title="Anonymous (no key)" quota="5 / hour" note="per IP address" />
                    <QuotaCard title="Free plan key" quota="1,000 / day" note="per key" accent />
                    <QuotaCard title="Pro plan key" quota="Unlimited*" note="100,000 / day soft cap" gold />
                </div>
                <p className="text-xs text-muted-foreground mt-3">
                    Exceeded quotas return HTTP <code className="text-accent">429</code> with a JSON body describing which limit was hit.
                </p>
            </Section>

            {/* FOOTER CTA */}
            <div className="mt-16 text-center border-t border-white/10 pt-10">
                <p className="text-muted-foreground text-sm mb-4">Ready to build?</p>
                <Link to="/developers">
                    <Button className="rounded-full h-12 px-6 bg-primary hover:bg-primary/90 text-primary-foreground font-semibold">
                        <KeyRound className="h-4 w-4 mr-2" /> Create your API key
                    </Button>
                </Link>
                <p className="text-xs text-muted-foreground mt-6">
                    Questions? Email <a href="mailto:hello@real-check.org" className="text-primary underline">hello@real-check.org</a>
                </p>
            </div>
        </div>
    );
}

function Section({ title, children, testid, id }) {
    return (
        <section id={id} className="mb-14" data-testid={testid}>
            <h2 className="font-display text-2xl md:text-3xl text-accent mb-5 tracking-tight">{title}</h2>
            {children}
        </section>
    );
}

function CodeBlock({ code, lang }) {
    const [copied, setCopied] = useState(false);
    async function copy() {
        try {
            await navigator.clipboard.writeText(code);
            setCopied(true);
            toast.success("Copied");
            setTimeout(() => setCopied(false), 2000);
        } catch (e) {
            toast.error("Copy failed — select manually");
        }
    }
    return (
        <div className="relative group">
            <button
                onClick={copy}
                className="absolute top-3 right-3 p-2 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 opacity-70 group-hover:opacity-100 transition z-10"
                aria-label="Copy code"
            >
                {copied ? <Check className="h-4 w-4 text-emerald-400" /> : <Copy className="h-4 w-4 text-muted-foreground" />}
            </button>
            <pre className="bg-black/50 border border-white/10 rounded-xl p-4 pr-14 text-[13px] leading-relaxed text-accent overflow-x-auto font-mono">
                <code>{code}</code>
            </pre>
            {lang && (
                <span className="absolute bottom-3 right-4 text-[10px] uppercase tracking-widest text-muted-foreground/60">
                    {lang}
                </span>
            )}
        </div>
    );
}

function QuotaCard({ title, quota, note, accent, gold }) {
    return (
        <Card
            className={`card-surface rounded-xl p-5 relative overflow-hidden ${accent ? "border border-primary/30" : ""} ${gold ? "border border-amber-400/40" : ""}`}
        >
            <div className={`absolute -top-6 -right-6 w-20 h-20 rounded-full blur-2xl ${gold ? "bg-amber-400/20" : accent ? "bg-primary/20" : "bg-white/5"}`} />
            <div className="relative">
                <p className="text-xs uppercase tracking-widest text-muted-foreground mb-2">{title}</p>
                <div className={`font-display text-3xl font-bold tabular-nums ${gold ? "text-amber-300" : "text-accent"}`}>{quota}</div>
                <p className="text-xs text-muted-foreground mt-2">{note}</p>
            </div>
        </Card>
    );
}
